import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { cartService } from '../services/cart.service';
import {
  setCart,
  updateItemQuantity,
  removeItem,
  clearCart as clearCartAction,
  setLoading,
  setError,
  selectCart,
  selectCartItems,
  selectCartSummary,
} from '../store/slices/cartSlice';
import { useToast } from '../components/common/Toast';

/**
 * Cart hook - Provides cart operations without automatic data fetching
 * Use useCartLoader() in your layout/app for automatic cart loading
 */
export const useCart = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const cart = useSelector(selectCart);
  const items = useSelector(selectCartItems);
  const summary = useSelector(selectCartSummary);

  /**
   * Fetch cart data from server
   * Call this explicitly when you need to refresh cart data
   */
  const fetchCart = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const response = await cartService.getCart();
      dispatch(setCart(response.data));
      return response.data;
    } catch (error) {
      if (error.response?.status !== 401) {
        const errorMessage = error.response?.data?.message || 'Failed to load cart';
        dispatch(setError(errorMessage));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }, [dispatch]);

  /**
   * Add item to cart
   */
  const addToCart = useCallback(async (productId, quantity = 1) => {
    dispatch(setLoading(true));
    try {
      await cartService.addToCart(productId, quantity);
      // Fetch updated cart after adding
      await fetchCart();
      toast.success('Item added to cart');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add item';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      dispatch(setLoading(false));
    }
  }, [dispatch, fetchCart, toast]);

  /**
   * Update cart item quantity with optimistic updates
   */
  const updateQuantity = useCallback(async (itemId, quantity) => {
    const originalItems = [...items];
    const originalSummary = { ...cart.summary };
    
    // Optimistic update
    dispatch(updateItemQuantity({ itemId, quantity }));
    
    try {
      await cartService.updateCartItem(itemId, quantity);
      // Fetch to get accurate server state
      await fetchCart();
    } catch (error) {
      // Rollback on error
      dispatch(setCart({ 
        cart: { items: originalItems }, 
        summary: originalSummary 
      }));
      const errorMessage = error.response?.data?.message || 'Failed to update quantity';
      toast.error(errorMessage);
    }
  }, [items, cart.summary, dispatch, fetchCart, toast]);

  /**
   * Remove item from cart
   */
  const removeFromCart = useCallback(async (itemId) => {
    dispatch(setLoading(true));
    try {
      await cartService.removeFromCart(itemId);
      dispatch(removeItem(itemId));
      await fetchCart();
      toast.success('Item removed from cart');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to remove item';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      dispatch(setLoading(false));
    }
  }, [dispatch, fetchCart, toast]);

  /**
   * Clear entire cart
   * @param {boolean} silent - Whether to bypass the confirmation prompt and success toast
   */
  const clearCart = useCallback(async (silent = false) => {
    if (!silent && !window.confirm('Are you sure you want to clear your cart?')) return;
    
    dispatch(setLoading(true));
    try {
      await cartService.clearCart();
      dispatch(clearCartAction());
      if (!silent) {
        toast.success('Cart cleared');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to clear cart';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, toast]);

  /**
   * Validate cart items (check stock, prices, etc.)
   */
  const validateCart = useCallback(async () => {
    try {
      const response = await cartService.validateCart();
      return response.data;
    } catch (error) {
      toast.error('Failed to validate cart');
      return { valid: false, errors: [] };
    }
  }, [toast]);

  return {
    // State
    cart: cart.cart,
    items,
    summary,
    loading: cart.loading,
    error: cart.error,
    
    // Actions
    fetchCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    validateCart,
  };
};

export default useCart;